import { useCallback, useEffect, useMemo, useState } from "react"
import type { UseQueryStatesKeysMap } from "nuqs"

export const DEFAULT_FILTER_REQUEST_DEBOUNCE_MS = 3_000
const FILTER_VALUE_DELIMITER = ","

export type QueryHistoryMode = "push" | "replace"
export type QueryFilterPatch<TFilters extends object> = Partial<{
  [Key in keyof TFilters]: TFilters[Key] | null
}>

export interface UrlFilterDefinition<TValue> {
  defaultValue: TValue
  debounce?: boolean
  resetPageOnChange?: boolean
}

export type UrlFilterDefinitions<TFilters extends object> = {
  [Key in keyof TFilters]: UrlFilterDefinition<TFilters[Key]>
}

export interface UrlQueryBooleanMutexGroup<TFilters extends object> {
  activeKey: keyof TFilters
  inactiveKeys: readonly (keyof TFilters)[]
}

type UrlQuerySetter<TFilters extends object> = (
  updates: QueryFilterPatch<TFilters>,
  options?: { history?: QueryHistoryMode },
) => unknown

interface UseUrlFiltersOptions<TFilters extends object, TPageKey extends keyof TFilters> {
  setQueryFilters: UrlQuerySetter<TFilters>
  pageKey: TPageKey
  firstPageValue: NonNullable<TFilters[TPageKey]>
  totalPages?: number
  mutexBooleanGroups?: readonly UrlQueryBooleanMutexGroup<TFilters>[]
}

export interface UpdateFilterOptions {
  history?: QueryHistoryMode
  resetPage?: boolean
}

const clampPage = (page: number, totalPages?: number): number => {
  const highestPage = totalPages ? Math.max(1, totalPages) : undefined
  const minimumPage = Math.max(1, page)
  return highestPage ? Math.min(minimumPage, highestPage) : minimumPage
}

export const coerceQueryLiteral = <TValue extends string>(
  values: readonly TValue[],
  value: string | undefined,
  fallback: TValue,
): TValue => {
  return values.includes(value as TValue) ? (value as TValue) : fallback
}

export const parseCommaSeparatedFilterValue = (value: string | null | undefined): string[] => {
  const uniqueValues = new Set<string>()

  value
    ?.split(FILTER_VALUE_DELIMITER)
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((item) => uniqueValues.add(item))

  return Array.from(uniqueValues)
}

export const formatCommaSeparatedFilterValue = (values: readonly string[]): string => {
  return parseCommaSeparatedFilterValue(values.join(FILTER_VALUE_DELIMITER)).join(FILTER_VALUE_DELIMITER)
}

export const normalizeCommaSeparatedFilterValue = (
  value: string | null | undefined,
  allowedValues?: readonly string[],
): string => {
  const values = parseCommaSeparatedFilterValue(value).filter((item) => !allowedValues || allowedValues.includes(item))

  return formatCommaSeparatedFilterValue(values)
}

export const hasCommaSeparatedFilterValue = (currentValue: string, value: string): boolean => {
  return parseCommaSeparatedFilterValue(currentValue).includes(value)
}

export const removeCommaSeparatedFilterValue = (currentValue: string, value: string): string => {
  return formatCommaSeparatedFilterValue(parseCommaSeparatedFilterValue(currentValue).filter((item) => item !== value))
}

export const toggleCommaSeparatedFilterValue = (currentValue: string, value: string): string => {
  if (hasCommaSeparatedFilterValue(currentValue, value)) {
    return removeCommaSeparatedFilterValue(currentValue, value)
  }

  return formatCommaSeparatedFilterValue([...parseCommaSeparatedFilterValue(currentValue), value])
}

export const defineUrlFilterParsers = <TParsers extends UseQueryStatesKeysMap>(parsers: TParsers): TParsers => {
  return parsers
}

export const defineUrlFilterConfig = <TFilters extends object>(
  config: UrlFilterDefinitions<TFilters>,
): UrlFilterDefinitions<TFilters> => {
  return config
}

export const getUrlFilterDefaults = <TFilters extends object>(config: UrlFilterDefinitions<TFilters>): TFilters => {
  const entries = Object.entries(config) as [keyof TFilters, UrlFilterDefinition<TFilters[keyof TFilters]>][]

  return Object.fromEntries(entries.map(([key, definition]) => [key, definition.defaultValue])) as TFilters
}

export const getDebouncedFilterKeys = <TFilters extends object>(
  config: UrlFilterDefinitions<TFilters>,
): (keyof TFilters)[] => {
  const entries = Object.entries(config) as [keyof TFilters, UrlFilterDefinition<TFilters[keyof TFilters]>][]

  return entries.filter(([, definition]) => definition.debounce).map(([key]) => key)
}

export const useDebouncedValue = <TValue>(value: TValue, delayMs: number): TValue => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value)
    }, delayMs)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [delayMs, value])

  return debouncedValue
}

export const useDebouncedQueryInput = <TInput extends Record<string, unknown>>(
  input: TInput,
  delayMs: number = DEFAULT_FILTER_REQUEST_DEBOUNCE_MS,
  debouncedKeys?: readonly (keyof TInput)[],
) => {
  const debouncedInput = useDebouncedValue(input, delayMs)
  const mergedInput = useMemo(() => {
    if (!debouncedKeys) return debouncedInput

    const nextInput = { ...input }
    debouncedKeys.forEach((key) => {
      nextInput[key] = debouncedInput[key]
    })

    return nextInput
  }, [debouncedInput, debouncedKeys, input])
  const isDebouncing = debouncedKeys
    ? debouncedKeys.some((key) => debouncedInput[key] !== input[key])
    : debouncedInput !== input

  return {
    debouncedInput: mergedInput,
    isDebouncing,
  }
}

export const useUrlFilters = <TFilters extends object, TPageKey extends keyof TFilters>({
  setQueryFilters,
  pageKey,
  firstPageValue,
  totalPages,
  mutexBooleanGroups = [],
}: UseUrlFiltersOptions<TFilters, TPageKey>) => {
  const updateFilters = useCallback(
    (updates: QueryFilterPatch<TFilters>, options: UpdateFilterOptions = {}) => {
      const nextUpdates: QueryFilterPatch<TFilters> = { ...updates }
      const writableUpdates = nextUpdates as Partial<Record<keyof TFilters, unknown>>

      mutexBooleanGroups.forEach(({ activeKey, inactiveKeys }) => {
        if (updates[activeKey] !== true) return

        inactiveKeys.forEach((inactiveKey) => {
          writableUpdates[inactiveKey] = false
        })
      })

      if (options.resetPage ?? true) {
        writableUpdates[pageKey] = firstPageValue
      }

      void setQueryFilters(nextUpdates, { history: options.history ?? "replace" })
    },
    [firstPageValue, mutexBooleanGroups, pageKey, setQueryFilters],
  )

  const setPage = useCallback(
    (page: number, options: Pick<UpdateFilterOptions, "history"> = {}) => {
      void setQueryFilters({ [pageKey]: clampPage(page, totalPages) } as QueryFilterPatch<TFilters>, {
        history: options.history ?? "push",
      })
    },
    [pageKey, setQueryFilters, totalPages],
  )

  return {
    setPage,
    updateFilters,
  }
}
